import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createAlert } from "components/Alert/Alert";
import { useAxiosWithAuth } from "Hooks/useAxiosWithAuth";
import { useNavigate } from "react-router-dom";


export const useClientProduct = (productId) => {
    const api = useAxiosWithAuth();

    const getData = async ({ queryKey }) => {
        const [_key, productId] = queryKey;

        const { data } = await api.get(`user/product/${productId}`);
        return data;
    };

    return useQuery({
        refetchOnWindowFocus: false,
        queryKey: ['client-product', productId],
        queryFn: getData,
        enabled: !!productId,
        retry: false,
    });
};

/**
 * @param {string|number} id - product id
 * @param {{ navigateAfterCreate?: boolean, onCreated?: (orderId: unknown) => void }} [options]
 * navigateAfterCreate: افتراضي true (توجيه لطلباتي). عيّن false لعرض التفاصيل في نفس الصفحة (مؤقت).
 */
export const useCreateOrder = (id, options = {}) => {
    const { navigateAfterCreate = true, onCreated } = options;
    const api = useAxiosWithAuth();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const _add = async (formData) => {
        const { data } = await api.post(`user/create-order/${id}`, formData);
        return data;
    };

    return useMutation({
        mutationFn: _add,
        onSuccess: (response) => {
            const { data, type, message } = response;
            createAlert(type, message);
            if (typeof onCreated === "function") {
                onCreated(data);
            }
            if (navigateAfterCreate) {
                navigate("/control-panel/my-orders?orderId=" + data);
            }
            queryClient.invalidateQueries({ queryKey: ["my-balance"], exact: true });
            queryClient.invalidateQueries({ queryKey: ["my-orders"] });
        },
        onError: (error) => {
            console.error("Error updating data:", error.message);
        },
        retry: false,
    });
};

export const useMyOrders = (perPage = 10, page = 1, name = "", state = "") => {
    const api = useAxiosWithAuth();

    const getData = async ({ queryKey }) => {
        const [_key, perPage, page, name, state] = queryKey;

        const { data } = await api.get('user/my-orders', {
            params: {
                perPage,
                page,
                name, state
            },
        });
        return data;
    };

    return useQuery({
        refetchOnWindowFocus: false,
        queryKey: ['my-orders', perPage, page, name, state],
        queryFn: getData,
        retry: false,
    });
};

export const useMyOrderById = (id) => {
    const api = useAxiosWithAuth();

    const getData = async ({ queryKey }) => {
        const [_key, id] = queryKey;

        const { data } = await api.get(`user/my-order/${id}`);
        return data;
    };

    return useQuery({
        refetchOnWindowFocus: false,
        queryKey: ['my-order-by-id', id],
        queryFn: getData,
        enabled: !!id,
        retry: false,
    });
};
// kossay start: hooks الأرقام المحفوظة (جلب / إضافة / حذف)
export const useSavedNumbers = (enabled = true) => {
    const api = useAxiosWithAuth();

    const getData = async () => {
        const { data } = await api.get('user/saved-numbers');
        return data;
    };

    return useQuery({
        refetchOnWindowFocus: false,
        queryKey: ['saved-numbers'],
        queryFn: getData,
        enabled,
        retry: false,
    });
};

export const useAddSavedNumber = () => {
    const api = useAxiosWithAuth();
    const queryClient = useQueryClient();

    const _add = async (formData) => {
        const { data } = await api.post('user/saved-numbers', formData);
        return data;
    };

    return useMutation({
        mutationFn: _add,
        onSuccess: (response) => {
            createAlert(response.type, response.message);
            queryClient.invalidateQueries({ queryKey: ['saved-numbers'] });
        },
        onError: (error) => {
            console.error("Error adding saved number:", error.message);
        },
        retry: false,
    });
};

export const useDeleteSavedNumber = () => {
    const api = useAxiosWithAuth();
    const queryClient = useQueryClient();

    const _delete = async (id) => {
        const { data } = await api.delete(`user/saved-numbers/${id}`);
        return data;
    };

    return useMutation({
        mutationFn: _delete,
        onSuccess: (response) => {
            createAlert(response.type, response.message);
            queryClient.invalidateQueries({ queryKey: ['saved-numbers'] });
        },
        onError: (error) => {
            console.error("Error deleting saved number:", error.message);
        },
        retry: false,
    });
};
// kossay end
